import { useState } from 'react';
import { useRouter } from 'next/router';
import { toEndOfUtcDay } from '@chainflip/utils/date';
import { UTCDate } from '@date-fns/utc';
import NoStrategiesSvg from '@/shared/assets/svg/no-strategies.svg';
import { CustomNumberFlow } from '@/shared/components/CustomNumberFlow';
import { Divider } from '@/shared/components/Divider';
import Button from '@/shared/components/flip-ui-kit/Button';
import Card from '@/shared/components/flip-ui-kit/Card';
import { StrategiesIntroductionModal } from '@/shared/components/StrategiesIntroductionModal';
import { StrategyTemplateCard } from '@/shared/components/strategy/StrategyTemplateCard';
import { FIRST_SWAP_DATE } from '@/shared/constants';
import { useActiveStrategies } from '@/shared/hooks/useActiveStrategies';
import useBoolean from '@/shared/hooks/useBoolean';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import { useStrategyStats } from '@/shared/hooks/useStrategyStats';
import { useStrategyTemplates } from '@/shared/hooks/useStrategyTemplates';
import useTracking from '@/shared/hooks/useTracking';
import ArrowIcon from '@/shared/icons/flip-ui-kit/large/Arrow';
import ChartCheckIcon from '@/shared/icons/flip-ui-kit/large/ChartCheck';
import DropletIcon from '@/shared/icons/flip-ui-kit/large/Droplet';
import MoneyBagIcon from '@/shared/icons/flip-ui-kit/large/MoneyBag';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/large/PlusCircle';
import { apyToText } from '@/shared/utils/boost';
import { getTimeframeOrFormattedDateLabels, localDayToUTCDay } from '@/shared/utils/date';
import { formatUsdValue } from '@/shared/utils/numbers';
import {
  type StrategyTemplate,
  type TradingStrategy,
  getStrategyTemplateUrl,
  isAllowedStrategyType,
  templateKeySchema,
} from '@/shared/utils/strategies';
import { StrategyBackground } from '../../assets/frames/StrategyBackground';
import Layout from '../../components/Layout';
import { LpFundAccountButton } from '../../components/LpFundAccountButton';
import AddLiquidityToStrategyModal from '../../components/Strategies/AddLiquidityToStrategyModal';
import CreateStrategyModal from '../../components/Strategies/CreateStrategyModal';
import { useStateChainAccount } from '../../hooks/useStateChainAccount';
import { LiquidityProvisionEvents, type LiquidityProvisionTrackEvents } from '../../types/track';

const TopStrategyCards = ({
  topApyTemplates,
  pillsByTemplateKey,
  setTemplateToCopy,
  setStrategyToAddLiquidity,
}: {
  topApyTemplates: StrategyTemplate[];
  pillsByTemplateKey?: Record<string, ('popular' | 'volume' | 'apy')[] | undefined>;
  setTemplateToCopy: (template: StrategyTemplate | undefined) => void;
  setStrategyToAddLiquidity: (strategy: TradingStrategy | undefined) => void;
}) => {
  const router = useRouter();
  const { selectedAccount } = usePolkadot(true);
  const { totalBalance } = useStateChainAccount();
  const accountIsEmpty = totalBalance !== null ? totalBalance === 0 : undefined;
  const track = useTracking<LiquidityProvisionTrackEvents>();

  return (
    <>
      {topApyTemplates.map((template) => (
        <StrategyTemplateCard
          key={template.templateKey}
          pill={pillsByTemplateKey?.[template.templateKey]?.at(0)}
          template={template}
          accountIsEmpty={accountIsEmpty}
          onClick={() => router.push(getStrategyTemplateUrl(template))}
          onAddLiquidity={() => {
            track(LiquidityProvisionEvents.AddAssLiquidity, {
              props: {
                path: window.location.pathname,
                position: 'ass_top_cards',
                scAccount: selectedAccount.idSs58,
                asset: template.baseAsset,
                key: template.templateKey,
                amount: template.totalBaseLiquidityUsd?.toString() ?? '',
                estApy: template.reportingData?.apyPercent.toString() ?? '',
              },
            });
            setStrategyToAddLiquidity(template.ownInstance);
          }}
          onCreateStrategy={() => {
            track(LiquidityProvisionEvents.CopyAssStrategy, {
              props: {
                path: window.location.pathname,
                position: 'ass_top_cards',
                scAccount: selectedAccount.idSs58,
                asset: template.baseAsset,
                key: template.templateKey,
              },
            });
            setTemplateToCopy(template);
          }}
          onViewDetails={() => router.push(getStrategyTemplateUrl(template))}
        />
      ))}
    </>
  );
};

export default function StrategiesPage() {
  const router = useRouter();
  const track = useTracking<LiquidityProvisionTrackEvents>();
  const { selectedAccount: lpAccount } = usePolkadot(true);

  const overallStartDate = new UTCDate(FIRST_SWAP_DATE);
  const overallEndDate = toEndOfUtcDay(localDayToUTCDay(new Date()));

  const { data: activeStrategies = [], isLoading: isActiveStrategiesLoading } =
    useActiveStrategies();
  const templatesData = useStrategyTemplates(activeStrategies, lpAccount);

  const {
    volumeFilled,
    pnl,
    isLoading: statsIsLoading,
  } = useStrategyStats(overallStartDate, overallEndDate);

  const totalLiquidity = activeStrategies.reduce(
    (acc, strategy) => acc + strategy.totalLiquidityUsd,
    0,
  );

  const {
    value: createStrategyModalOpen,
    setTrue: openCreateStrategyModal,
    setFalse: closeCreateStrategyModal,
  } = useBoolean(false);

  const [templateToCopy, setTemplateToCopy] = useState<StrategyTemplate | undefined>(undefined);
  const [strategyToAddLiquidity, setStrategyToAddLiquidity] = useState<TradingStrategy | undefined>(
    undefined,
  );

  const templateToCopyDetails = templateToCopy
    ? templateKeySchema.parse(templateToCopy.templateKey)
    : undefined;

  const hasTopApyStrategies = (templatesData?.topByApy ?? []).length > 0;
  return (
    <Layout title="Stablecoin Strategies">
      <div className="relative z-[2] flex w-full flex-col gap-y-6">
        <StrategyBackground className="pointer-events-none absolute top-[-80px] z-[-1] h-[400px] w-full" />
        <div className="flex flex-col items-center gap-y-3.5 self-center text-center font-aeonikMedium sm:w-[430px]">
          <img src="/illustration-stablecoin.png" alt="Stablecoin Strategies Illustration" />
          <div className="text-[32px] leading-[normal] text-cf-light-4">
            <div className="flex items-center gap-2">
              Earn up to{' '}
              {
                <CustomNumberFlow
                  value={apyToText(templatesData?.highlighted.apy?.reportingData?.apyPercent ?? 0)}
                />
              }{' '}
              APY with
            </div>
            <div className="text-cf-blue-2">Stablecoin Strategies</div>
          </div>
          <div className="font-aeonikRegular text-16 leading-[normal] text-cf-light-2">
            Turn stablecoins into reliable yield with zero hassle.
          </div>
          <LpFundAccountButton
            className="md:w-[312px]"
            type="primary-standard"
            iconPos="left"
            icon={<PlusCircleIcon className="mr-1" />}
            size="medium"
            onClick={() => {
              track(LiquidityProvisionEvents.DeployAssStrategy, {
                props: {
                  path: window.location.pathname,
                  position: 'ass_landing',
                  scAccount: lpAccount.idSs58,
                },
              });
              openCreateStrategyModal();
            }}
          >
            Deploy New Strategy
          </LpFundAccountButton>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card
            icon={<DropletIcon className="text-cf-light-2" />}
            title="Deployed Liquidity"
            tooltip="The combined value (in USD) of all active stablecoin strategies."
            loading={isActiveStrategiesLoading}
            footer="Current"
          >
            <div className="flex h-full items-center text-16">{formatUsdValue(totalLiquidity)}</div>
          </Card>
          <Card
            icon={<ChartCheckIcon className="text-cf-light-2" />}
            title="Filled Volume"
            tooltip="Total value (in USD) of all filled stablecoin strategies trades to date."
            loading={statsIsLoading}
            footer={getTimeframeOrFormattedDateLabels([overallStartDate, overallEndDate])}
          >
            <div className="flex h-full items-center text-16">{formatUsdValue(volumeFilled)}</div>
          </Card>
          <Card
            icon={<MoneyBagIcon className="text-cf-light-2" />}
            title="Profit & Loss"
            tooltip="Estimated net returns from all stablecoin strategies, including profit and losses."
            loading={statsIsLoading}
            footer={getTimeframeOrFormattedDateLabels([overallStartDate, overallEndDate])}
          >
            <div className="flex h-full items-center text-16">
              {formatUsdValue(pnl, undefined, true)}
            </div>
          </Card>
        </div>
        <Divider className="h-px" />
        <div className="flex flex-row justify-between">
          <div className="font-aeonikMedium text-24 text-cf-white">Top Strategies</div>
          <Button
            type="link-standard"
            iconPos="right"
            icon={<ArrowIcon direction="right" className="ml-1" />}
            onClick={(e) => {
              e.stopPropagation();
              router.push('/strategies/templates');
            }}
          >
            View All
          </Button>
        </div>
        {isActiveStrategiesLoading || hasTopApyStrategies ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <TopStrategyCards
              pillsByTemplateKey={templatesData?.pillsByTemplateKey}
              topApyTemplates={templatesData?.topByApy ?? []}
              setTemplateToCopy={setTemplateToCopy}
              setStrategyToAddLiquidity={setStrategyToAddLiquidity}
            />
          </div>
        ) : (
          <div className="flex h-[304px] w-full flex-col items-center justify-center gap-y-6 rounded-md border border-cf-gray-4 bg-cf-gray-3">
            <NoStrategiesSvg />
            <div className="font-aeonikRegular text-14 text-cf-light-3">No strategies yet</div>
          </div>
        )}
      </div>

      {templateToCopyDetails && isAllowedStrategyType(templateToCopyDetails.type) && (
        <CreateStrategyModal
          onClose={() => {
            setTemplateToCopy(undefined);
          }}
          open
          initialMinBuyTick={templateToCopyDetails.minBuyTick}
          initialMaxBuyTick={templateToCopyDetails.maxBuyTick}
          initialMinSellTick={templateToCopyDetails.minSellTick}
          initialMaxSellTick={templateToCopyDetails.maxSellTick}
          initialBaseAsset={templateToCopyDetails.baseAsset}
          initialStrategyType={templateToCopyDetails.type}
        />
      )}
      {strategyToAddLiquidity && (
        <AddLiquidityToStrategyModal
          strategy={strategyToAddLiquidity}
          onClose={() => setStrategyToAddLiquidity(undefined)}
          open
        />
      )}
      <CreateStrategyModal onClose={closeCreateStrategyModal} open={createStrategyModalOpen} />
      <StrategiesIntroductionModal />
    </Layout>
  );
}
